// Stripe integration — falls back to mock when no key is configured.
// The server (server/index.js) handles actual charge/payout logic.

import { supabase } from './supabase'
import { calculateGroupDates, makeCode } from './db'

const STRIPE_PK    = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
const API_BASE     = import.meta.env.VITE_API_URL || 'http://localhost:3001'

export const isStripeEnabled =
  !!STRIPE_PK && STRIPE_PK !== 'your_stripe_publishable_key'

// ── Wallet helpers (persisted locally when Stripe not configured) ─────────────

const WALLET_KEY = 'wb_wallet'

export function getLocalBalance() {
  return parseFloat(localStorage.getItem(WALLET_KEY) || '500.00')
}

export function setLocalBalance(amount) {
  localStorage.setItem(WALLET_KEY, String(amount.toFixed(2)))
}

// ── Mock deposit ──────────────────────────────────────────────────────────────

// TODO: Replace with real Stripe webhook integration. Returns a static
// success shape that PaymentModal's onSuccess no longer reads —
// onSuccess refetches Supabase truth directly. The newBalance: 0 is
// not a real value, just a stable shape for the unused return.
export async function mockDeposit(amount) {
  await new Promise(r => setTimeout(r, 1200)) // simulate latency
  return { success: true, newBalance: 0 }
}

// ── Real deposit (calls Express server → Stripe) ──────────────────────────────

export async function createDepositIntent(amount) {
  const r = await fetch(`${API_BASE}/create-payment-intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount: Math.round(amount * 100) }),
  })
  if (!r.ok) throw new Error('Payment server error')
  return r.json() // { clientSecret }
}

export async function createPayout(userId, amount) {
  const r = await fetch(`${API_BASE}/create-payout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, amount: Math.round(amount * 100) }),
  })
  if (!r.ok) throw new Error('Payout server error')
  return r.json()
}

// ── Charge entry fee from wallet ──────────────────────────────────────────────

export function chargeEntryFee(fee) {
  const bal = getLocalBalance()
  if (bal < fee) throw new Error('Insufficient balance')
  setLocalBalance(bal - fee)
  return { success: true, newBalance: bal - fee }
}

// ── Stripe creator-fee wrappers (backlog 25, Phase C) ──────────────────────
// Frontend wrappers for the four /api/* endpoints shipped in Phase B
// (server/index.js). Each authenticated wrapper:
//   - reads the Supabase JWT via supabase.auth.getSession()
//   - attaches `Authorization: Bearer <jwt>`
//   - returns parsed JSON or throws on non-2xx
// No live callers in src/ until Phase D wires CreateGroupModal — these
// wrappers ship dormant.

// Client mirror of TIER_MAX / TIER_AMOUNTS in server/index.js. Server is
// authoritative for the actual charge (Stripe price IDs from Dashboard);
// this drives UI + client-side max_players. Keep in sync — a drift here
// shows wrong prices/caps in the modal but can't cause a wrong charge
// (server validates max_players === tier cap and rejects on mismatch).
// Labels are NOT stored here; Phase D composes the display string from
// (index, t.maxPlayers) so each tier's cap number lives in exactly one place.
export const TIERS = [
  { id: 'tier_1', fee: 5,  cents: 500,  maxPlayers: 10  },
  { id: 'tier_2', fee: 10, cents: 1000, maxPlayers: 25  },
  { id: 'tier_3', fee: 20, cents: 2000, maxPlayers: 50  },
  { id: 'tier_4', fee: 30, cents: 3000, maxPlayers: 100 },
]

// ── Phase 3 create-modal display: contest-class price mirror ───────────
//
// Replaces the dead TIERS-based size-tier picker that the modal used to
// surface. The 2026-06-12 PM re-tier moved pricing off pool-size and onto
// contest_class × size_class — these constants drive the modal's
// derived-price display only. The buy flow (createCheckoutSession + the
// server endpoints) stays gated behind the STRIPE_PAYMENTS_ENABLED flag
// and uses server/index.js as the authoritative source; this file is
// display-only.
//
// ╔══════════════════════════════════════════════════════════════════════╗
// ║ DRIFT TRAP 2026-06-13 PM — three drift-prone tables here are also    ║
// ║ DUPLICATED in server/index.js for the authoritative charge path:     ║
// ║   • CONTEST_CLASS_BASE_CENTS     (6 entries — per-class base price)  ║
// ║   • SIZE_SURCHARGE_CENTS         (3 entries — per-size surcharge)    ║
// ║   • size-tier boundaries         (LARGE_THRESHOLD, EXTRA_LARGE_      ║
// ║                                    THRESHOLD, HARD_CAP_SEATS)        ║
// ║                                                                      ║
// ║ Phase D re-tier (2026-06-13 PM) moved 2 tiers → 3 (standard 4-25,    ║
// ║ large 26-100, extra_large 101-500) and the hard cap 250 → 500.       ║
// ║ Total prices 12 → 18. Both files MUST agree exactly with each        ║
// ║ other AND with migration 038's RPC ranges                            ║
// ║ (migrations/038_size_class_extra_large.sql lines 223-237).           ║
// ║ Boundary drift = SIZE_MISMATCH at consume time.                      ║
// ║                                                                      ║
// ║ Same risk shape as the mainCard.js two-copy pattern (server/lib/     ║
// ║ mainCard.js + src/lib/mainCard.js — bit us under Railway outage      ║
// ║ 2026-06-12). Lower-stakes than mainCard (a stale client mirror can   ║
// ║ only mis-DISPLAY, not mis-CHARGE — server price IDs from Stripe      ║
// ║ Dashboard are the source of truth at charge time), but a stale       ║
// ║ display sows user confusion AND a stale deriveSizeClass below can    ║
// ║ silently send a 90-player pool down the 'large' path with a          ║
// ║ 'standard' entitlement → server-side ENTITLEMENT_SIZE_MISMATCH at    ║
// ║ the consume step.                                                    ║
// ║                                                                      ║
// ║ ANY price/boundary change MUST be made in BOTH:                      ║
// ║   • src/lib/stripe.js        (this file — client modal display)      ║
// ║   • server/index.js          (authoritative — Stripe Dashboard +     ║
// ║                                  amount_cents at charge time)        ║
// ║   • migrations/<NN>_*.sql    (if boundaries change — RPC is the      ║
// ║                                  charge-time source of truth)        ║
// ║                                                                      ║
// ║ Phase D may collapse this when wiring the real buy flow — likely     ║
// ║ by serving the price catalog from /api/config so the modal can       ║
// ║ fetch it. Until that lands, keep all three sources in sync.          ║
// ╚══════════════════════════════════════════════════════════════════════╝
export const CONTEST_CLASS_BASE_CENTS = {
  daily_pickem:      299,
  ufc_event:         499,
  weekly_pickem:     799,
  golf_pickem:       799,   // Golf:divisions provisionally rides this rate
                            // per migration 036's sport=Golf dispatch — see
                            // HANDOFF "Phase 3 verification status" carry-
                            // forward for the operator-confirm flag.
  bracket_playoffs:  999,
  survivor_season:  1499,
}

// Per-size surcharge MAP (Phase D re-tier 2026-06-13 PM — was a scalar
// `= 500`, now a 3-entry object mirroring server SIZE_CLASS_SURCHARGE_CENTS).
// Authoritative size-bracket math lives in the create-RPC (migration 038
// derives standard / large / extra_large from p_max_players). Keys here
// MUST match the server map and the RPC's CASE branches.
export const SIZE_SURCHARGE_CENTS = { standard: 0, large: 500, extra_large: 1000 }

export const FREE_SEAT_CEILING       = 3     // pools of ≤3 seats are free
export const LARGE_THRESHOLD         = 25    // 26+ seats → "large" size class
export const EXTRA_LARGE_THRESHOLD   = 100   // 101+ seats → "extra_large" size class
export const HARD_CAP_SEATS          = 500   // operator-locked maximum

// Pure client mirror of migration 038's size_class CASE
// (migrations/038_size_class_extra_large.sql:223-237). Returns the
// size_class that a (free-or-paid) pool of `maxPlayers` seats would map to.
// Returns 'free' for ≤3, null for >HARD_CAP_SEATS (UI should reject), and
// one of 'standard' / 'large' / 'extra_large' for paid sizes.
//
// Boundaries (MUST match the RPC exactly — drift = SIZE_MISMATCH):
//   ≤   3   →  'free'         (no entitlement, no Stripe)
//     4-25  →  'standard'     (+$0)
//    26-100 →  'large'        (+$5)
//   101-500 →  'extra_large'  (+$10)
//   > 500   →  null           (UI must reject before submit)
export function deriveSizeClass(maxPlayers) {
  if (!Number.isFinite(maxPlayers) || maxPlayers <= FREE_SEAT_CEILING) return 'free'
  if (maxPlayers <= LARGE_THRESHOLD)       return 'standard'        // 4-25
  if (maxPlayers <= EXTRA_LARGE_THRESHOLD) return 'large'           // 26-100
  if (maxPlayers <= HARD_CAP_SEATS)        return 'extra_large'     // 101-500
  return null                                                        // > 500: reject
}

// Mirrors server/index.js's contest-class derivation
// (migrations/036_create_group_from_entitlement.sql lines 138-161).
// Pure function over form state. Returns null when the inputs aren't
// enough to pick a class — caller renders a placeholder, not a guess.
//
// Mapping (operator-approved 2026-06-12 PM re-tier):
//   sport=UFC            → 'ufc_event'         (any contest_type)
//   sport=Golf           → 'golf_pickem'       (any contest_type — incl.
//                                                divisions, see DRIFT
//                                                comment above)
//   contest_type=bracket → 'bracket_playoffs'  (any other sport)
//   contest_type=pick_em → daily/weekly/survivor by pool_days
//                            pool_days <= 1     → daily_pickem
//                            pool_days 2-9      → weekly_pickem
//                            pool_days >= 10    → survivor_season
//   other                → null  (placeholder in UI)
export function deriveContestClass({ sport, contest_type, start_date, end_date }) {
  if (sport === 'UFC')               return 'ufc_event'
  if (sport === 'Golf')              return 'golf_pickem'
  if (contest_type === 'bracket')    return 'bracket_playoffs'
  if (contest_type === 'pick_em' && start_date && end_date) {
    const startMs = Date.parse(start_date)
    const endMs   = Date.parse(end_date)
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null
    const poolDays = Math.floor((endMs - startMs) / 86_400_000) + 1  // inclusive
    if (poolDays <= 1)                  return 'daily_pickem'
    if (poolDays >= 2 && poolDays <= 9) return 'weekly_pickem'
    if (poolDays >= 10)                 return 'survivor_season'
  }
  return null
}

// Display label for the modal price tile (no charge logic depends on this).
export const CONTEST_CLASS_LABELS = {
  daily_pickem:      "daily pick'em",
  ufc_event:         "UFC event",
  weekly_pickem:     "weekly pick'em",
  golf_pickem:       "golf pool",
  bracket_playoffs:  "playoff bracket",
  survivor_season:   "season-long survivor",
}

// Takes the modal's 12-field form payload and returns the complete 14-field
// poolConfig that /api/create-checkout-session requires (adds normalized
// start_date, end_date, invite_code). Pure function; no I/O. The server
// stamps `version` itself at INSERT time (Decision 3 in backlog-25-design.md)
// — do NOT set it here; any client-sent version is overwritten server-side.
//
// D-side contract: caller passes the POST-RESOLUTION duration_type /
// duration / start_round — the same object today's modal builds for the
// free-path createGroup call (e.g. duration_type = 'playoffs' for bracket
// pools, not the raw form field). createGroup resolves these internally;
// the paid path must resolve them upstream before calling this helper.
export function completePoolConfig(formData) {
  const { start, end } = calculateGroupDates(
    formData.duration_type, formData.sport, formData.start_date
  )
  return {
    ...formData,
    start_date:  start.toISOString().slice(0, 10),
    end_date:    end.toISOString().slice(0, 10),
    invite_code: makeCode(),
  }
}

// POST /api/create-checkout-session — initiates Stripe Checkout for a tier
// fee. Returns { checkoutUrl }; caller (Phase D) performs the redirect via
// `window.location = checkoutUrl`. Keeping the redirect out of the wrapper
// makes it testable and keeps the side effect at the UI layer.
export async function createCheckoutSession({ tier, poolConfig }) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')
  const r = await fetch(`${API_BASE}/api/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ tier, poolConfig }),
  })
  if (!r.ok) {
    const body = await r.json().catch(() => ({}))
    throw new Error(body.error || `Checkout failed (${r.status})`)
  }
  const { checkoutUrl } = await r.json()
  return { checkoutUrl }
}

// POST /api/checkout-session-unlock — Phase 1.ζ-1 freemium base-unlock
// wrapper. Mirrors createCheckoutSession's auth idiom exactly (Supabase
// JWT in Authorization header). The request body carries `group_id` ONLY:
// identity flows from the JWT, NEVER from the body — that's the C4 trust
// boundary documented in migrations/041_pool_unlocks.sql. Server stamps
// req.user.id into Stripe metadata; the webhook → unlock_group RPC chain
// trusts only that stamp.
//
// Returns { checkoutUrl } matching server/index.js's
// `res.json({ checkoutUrl: session.url })` at /api/checkout-session-unlock.
// Caller (1.ζ-2 UI) performs the redirect via `window.location = checkoutUrl`.
export async function createCheckoutSessionUnlock({ group_id }) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')
  const r = await fetch(`${API_BASE}/api/checkout-session-unlock`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ group_id }),
  })
  if (!r.ok) {
    const body = await r.json().catch(() => ({}))
    throw new Error(body.error || `Unlock checkout failed (${r.status})`)
  }
  const { checkoutUrl } = await r.json()
  return { checkoutUrl }
}

// GET /api/payment-status — polls until terminal status or timeout. Used by
// the return-from-Stripe handler (Phase E) to know when the webhook fired
// and the pool exists. 1s initial delay lets the webhook beat the redirect
// (instant first poll would almost always 404/pending, wasting a call);
// 2s interval over 30s = 15 calls max.
//
// Returns one of:
//   { status: 'succeeded',                                  createdPoolId: '<uuid>' }
//   { status: 'failed'|'expired'|'expired_schema_mismatch', createdPoolId: null }
//   { status: 'unknown',                                    createdPoolId: null }
//     // 404 (auth-identity edge or genuinely missing) — terminal but soft;
//     // pool may actually exist server-side. UI should surface "couldn't
//     // confirm — check My Pools" rather than a hard error.
//   { status: 'pending',                                    createdPoolId: null }
//     // timeout — webhook didn't resolve within 30s. UI shows "still
//     // processing, check My Pools."
export async function pollPaymentStatus(sessionId, opts = {}) {
  const { timeoutMs = 30000, intervalMs = 2000, initialDelayMs = 1000 } = opts
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')
  const headers = { Authorization: `Bearer ${session.access_token}` }
  const TERMINAL = new Set(['succeeded', 'failed', 'expired', 'expired_schema_mismatch'])
  const deadline = Date.now() + timeoutMs

  await new Promise(res => setTimeout(res, initialDelayMs))
  while (Date.now() < deadline) {
    const r = await fetch(
      `${API_BASE}/api/payment-status?session_id=${encodeURIComponent(sessionId)}`,
      { headers }
    )
    if (r.status === 404) return { status: 'unknown', createdPoolId: null }
    if (r.ok) {
      const { status, createdPoolId } = await r.json()
      if (TERMINAL.has(status)) return { status, createdPoolId }
    }
    // Non-ok non-404 (transient 500 / network blip): swallow and retry until deadline.
    await new Promise(res => setTimeout(res, intervalMs))
  }
  return { status: 'pending', createdPoolId: null }
}

// GET /api/config — boot-time read of backend feature flags. Fails closed:
// any network error or non-2xx returns { stripePaymentsEnabled: false } so
// the frontend assumes payments off when it can't reach the backend.
// Phase D reads this on app mount and caches in React state.
export async function getServerConfig() {
  try {
    const r = await fetch(`${API_BASE}/api/config`)
    if (!r.ok) return { stripePaymentsEnabled: false }
    return await r.json()
  } catch {
    return { stripePaymentsEnabled: false }
  }
}
